import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  reload,
  updateProfile,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../firebase";
import { getSafeAccessError } from "../api/platformAccessApi";
import { useAccess } from "../auth/AccessProvider";
import AccessAlert from "../components/AccessAlert";
import AccessShell from "../components/AccessShell";
import RegistrationAccountForm from "../components/RegistrationAccountForm";
import RegistrationStepper from "../components/RegistrationStepper";
import VerificationPanel from "../components/VerificationPanel";
import "./ApplicationRegisterPage.css";

const ACCOUNT_FORM = "account_form";
const EMAIL_VERIFICATION_REQUIRED = "email_verification_required";
const EMAIL_VERIFIED = "email_verified";
const VERIFICATION_POLL_MS = 5000;

function sendApplicantVerificationEmail() {
  return httpsCallable(functions, "sendApplicantVerificationEmail")({});
}

export default function ApplicationRegisterPage() {
  const { user } = useAccess();
  const [stage, setStage] = useState(auth.currentUser ? EMAIL_VERIFICATION_REQUIRED : ACCOUNT_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const verificationCheckInFlightForUser = useRef(null);
  const trackedUserUid = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const nextUid = user?.uid || null;
    if (trackedUserUid.current !== nextUid) {
      trackedUserUid.current = nextUid;
      verificationCheckInFlightForUser.current = null;
      setError("");
      setMessage("");
      setBusy(false);
      setStage(user ? EMAIL_VERIFICATION_REQUIRED : ACCOUNT_FORM);
      return;
    }
    if (!user) {
      setStage(ACCOUNT_FORM);
      return;
    }
    setStage((current) => current === ACCOUNT_FORM ? EMAIL_VERIFICATION_REQUIRED : current);
  }, [user]);

  const createAccount = async ({ firstName, surname, email, password }) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const name = `${firstName} ${surname}`.trim();
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(credential.user, { displayName: name });
      await sendApplicantVerificationEmail();
      setStage(EMAIL_VERIFICATION_REQUIRED);
      setMessage("Your account was created. Verify your email to continue.");
    } catch (nextError) {
      setError(getSafeAccessError(nextError, "We could not create your account. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const checkEmailVerification = useCallback(async ({ announce = false } = {}) => {
    if (stage !== EMAIL_VERIFICATION_REQUIRED || busy || !user) return;
    const uid = user.uid;
    if (verificationCheckInFlightForUser.current === uid) return;
    verificationCheckInFlightForUser.current = uid;
    try {
      await reload(user);
      if (!mounted.current || auth.currentUser?.uid !== uid) return;
      if (!user.emailVerified) {
        if (announce) {
          setError("Your email is not verified yet. Open the email, then try again.");
        }
        return;
      }
      setError("");
      setMessage("Your email is verified.");
      setStage(EMAIL_VERIFIED);
    } catch (nextError) {
      if (announce && mounted.current) {
        setError(getSafeAccessError(nextError, "We could not confirm your email yet. Please try again."));
      }
    } finally {
      if (verificationCheckInFlightForUser.current === uid) {
        verificationCheckInFlightForUser.current = null;
      }
    }
  }, [busy, stage, user]);

  useEffect(() => {
    if (stage !== EMAIL_VERIFICATION_REQUIRED) return undefined;
    const runCheck = () => {
      if (document.visibilityState === "visible") void checkEmailVerification();
    };
    runCheck();
    const interval = window.setInterval(runCheck, VERIFICATION_POLL_MS);
    window.addEventListener("focus", runCheck);
    document.addEventListener("visibilitychange", runCheck);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", runCheck);
      document.removeEventListener("visibilitychange", runCheck);
    };
  }, [checkEmailVerification, stage]);

  const resendVerification = async () => {
    if (!auth.currentUser) return;
    setBusy(true);
    setError("");
    try {
      await sendApplicantVerificationEmail();
      setMessage("A new verification email has been sent.");
    } catch (nextError) {
      setError(getSafeAccessError(nextError, "We could not resend verification. Please wait and try again."));
    } finally {
      setBusy(false);
    }
  };

  const activeStep = stage === ACCOUNT_FORM ? 0 : stage === EMAIL_VERIFICATION_REQUIRED ? 1 : 2;

  return (
    <AccessShell
      eyebrow="Apply"
      title="Create your account."
      description="Create your account to apply. Sign in later with the same login."
      footer={<p>Already have an account? <Link to="/login">Sign in</Link></p>}
    >
      <div className="application-register">
      <RegistrationStepper activeStep={activeStep} />
      <AccessAlert tone="error">{error}</AccessAlert>
      <AccessAlert tone="success">{message}</AccessAlert>

      {stage === ACCOUNT_FORM ? (
        <RegistrationAccountForm
          onSubmit={createAccount}
          submitting={busy}
          emailLabel="Your email"
          emailPlaceholder="name@email.com"
        />
      ) : null}

      {stage === EMAIL_VERIFICATION_REQUIRED ? (
        <VerificationPanel
          email={auth.currentUser?.email}
          onContinue={() => checkEmailVerification({ announce: true })}
          onResend={resendVerification}
          busy={busy}
          continueLabel="I have verified my email"
          note="Open the email from AndUs, verify your address, then return here to continue."
        />
      ) : null}

      {stage === EMAIL_VERIFIED ? (
        <div className="application-register-ready">
          <p>Your account is ready. Use the same sign-in page when you return.</p>
          <Link className="pa-primary-button" to="/login">
            <span>Sign in</span>
            <span aria-hidden="true">›</span>
          </Link>
        </div>
      ) : null}
      </div>
    </AccessShell>
  );
}
